import React from 'react';

import { SocialPostAttachment } from 'types/AmplifiedTemplate';

import MediaView from './MediaView';
import { isAttachmentPreviewable } from './isAttachmentPreviewable';

type AttachmentPreviewProps = {
  isPostPreview?: boolean;
  attachments: SocialPostAttachment[];
};

export default function AttachmentPreview({
  isPostPreview,
  attachments,
}: AttachmentPreviewProps) {
  const previewAbleAttachments: SocialPostAttachment[] = attachments.filter(
    (attachmentItem) => isAttachmentPreviewable(attachmentItem.attachment),
  );

  if (previewAbleAttachments.length === 0) {
    return null;
  }

  if (previewAbleAttachments.length === 1) {
    const item = previewAbleAttachments[0];
    return (
      <div className={`${isPostPreview ? '' : 'h-48'}`}>
        <MediaView item={item} isPostPreview={isPostPreview} />
      </div>
    );
  }

  if (previewAbleAttachments.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-1">
        <div className="w-full h-48 relative bg-gray-100 rounded-lg overflow-hidden">
          <MediaView item={previewAbleAttachments[0]} />
        </div>
        <div className="w-full h-48 relative bg-gray-100 rounded-lg overflow-hidden">
          <MediaView item={previewAbleAttachments[1]} />
        </div>
      </div>
    );
  }

  if (previewAbleAttachments.length === 3) {
    return (
      <div className="grid gap-1">
        <div className="w-full h-48 relative bg-gray-100 rounded-lg overflow-hidden">
          <MediaView item={previewAbleAttachments[0]} />
        </div>
        <div className="grid grid-cols-2 gap-1">
          <div className="w-full h-32 relative bg-gray-100 rounded-lg overflow-hidden">
            <MediaView item={previewAbleAttachments[1]} />
          </div>
          <div className="w-full h-32 relative bg-gray-100 rounded-lg overflow-hidden">
            <MediaView item={previewAbleAttachments[2]} />
          </div>
        </div>
      </div>
    );
  }

  if (previewAbleAttachments.length >= 4) {
    const secondLineItemCount = Math.min(previewAbleAttachments.length - 1, 3);
    const remainingCount = Math.max(0, previewAbleAttachments.length - 4);

    return (
      <div className="grid gap-1">
        <div className="w-full h-48">
          <MediaView item={previewAbleAttachments[0]} />
        </div>
        {secondLineItemCount > 0 && (
          <div className="grid grid-cols-3 gap-1">
            {Array.from({ length: secondLineItemCount }).map((_, index) => {
              const imageIndex = index + 1;
              const isLastItem =
                index === secondLineItemCount - 1 && remainingCount > 0;
              return (
                <div
                  key={`image-second-${index}`}
                  className="w-full h-28 relative bg-gray-100 rounded-lg overflow-hidden"
                >
                  <MediaView item={previewAbleAttachments[imageIndex]} />
                  {isLastItem && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="absolute inset-0 rounded-lg bg-black/40" />
                      <div className="relative z-10 w-8 h-8 text-white text-xl font-bold flex items-center justify-center">
                        +{remainingCount}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return null;
}
